import { Injectable, isDevMode } from "@angular/core";
import { Store } from "@ngrx/store";
import { AuthSelector, CoreHttpClient, CoreState } from "@obg/ngx.core";
import { CoreContentState, TranslationGroup, TranslationSelector, TranslationService } from "@obg/ngx.core.content";
import { DeviceConfigService, PlatformService, WindowRef } from "@obg/ngx.global";
import { Observable, combineLatest, of } from "rxjs";
import { catchError, map, shareReplay, switchMap } from "rxjs/operators";

import { SupportConfigService } from "./customer-support.config";
import { PtaString } from "./customer-support.model";

/**
 * CRS keys that points to the customer service URL for each device type.
 */
const CUSTOMER_SUPPORT_URL_KEY = {
	desktop: "general.customer-support-link",
	mobile: "general.customer-support-link.mobile",
} as const;

/**
 * Service which can be used to retrieve the URL for the customer support page,
 * or navigate to it.
 *
 * Use it when you need to programmatically navigate to the customer support page.
 *
 * Most of the time however you should use the `CustomerSupportLinkDirective`
 * when you want to have link pointing to the customer support page.
 *
 * @example
 * ```ts
 * class Component {
 * 	constructor(private customerSupportService: CustomerSupportService) {
 * 		this.customerSupportService.openSupportPageInNewTab()
 * 	}
 * }
 * ```
 */
@Injectable({
	providedIn: "root",
})
export class CustomerSupportService {
	private _pageURL: string | undefined = undefined;
	private _ptaString: string | undefined = undefined;

	/** URL for the customer support page. */
	get pageURL(): string {
		if (this._pageURL === undefined || this._pageURL === CUSTOMER_SUPPORT_URL_KEY.desktop) {
			this.loadPageURL();
		}
		return this._pageURL!;
	}

	get pageURL$(): Observable<string> {
		const urlTranslationKey = this.getPageURLTranslationKey();
		return this.coreContentStore.pipe(
			this.translationSelector.getByKeys_v2$([urlTranslationKey], [TranslationGroup.General], undefined),
			map(() => this.pageURL!)
		);
	}

	constructor(
		private deviceConfigService: DeviceConfigService,
		private translationSelector: TranslationSelector,
		private translationService: TranslationService,
		private windowRef: WindowRef,
		private platformService: PlatformService,
		private coreContentStore: Store<CoreContentState>,
		private client: CoreHttpClient,
		private config: SupportConfigService,
		private store: Store<CoreState>
	) {}

	openSupportPageInNewTab() {
		this.windowRef.native.open(this.pageURL, "_blank");
	}

	/** Returns whether the given URL point to the customer support page. */
	isSupportURL(url: string) {
		try {
			const givenURLHost = new URL(url).host;
			const supportURLHost = new URL(this.pageURL).host;
			return givenURLHost === supportURLHost;
		} catch {
			return false;
		}
	}

	getPtaString$: Observable<string | undefined> = this.store.select(AuthSelector.getSessionToken).pipe(
		switchMap((sessionToken) => {
			if (sessionToken !== undefined) {
				if (this._ptaString !== undefined) {
					return of(this._ptaString);
				}
				return this.client
					.get<PtaString>({
						url: `${this.config.getPtaStringEndpoint()}`,
						headers: {
							sessionToken: sessionToken!,
						},
					})
					.pipe(
						map((ptaString) => {
							this._ptaString = ptaString.encryptedPtaString;
							return ptaString.encryptedPtaString;
						}),
						catchError(() => {
							this._ptaString = undefined;
							return of(undefined);
						})
					);
			}
			this._ptaString = undefined;
			return of(undefined);
		}),
		shareReplay()
	);

	customerSupportUrl$: Observable<string> = combineLatest([this.getPtaString$, this.pageURL$]).pipe(
		map(([ptaString, url]) => (ptaString ? `${url}?pta=${ptaString}` : url))
	);

	private loadPageURL() {
		const urlTranslationKey = this.getPageURLTranslationKey();
		const pageURL = this.translationService.getByKey(urlTranslationKey);

		const couldNotRetrievePageUrl = pageURL === urlTranslationKey;
		if (isDevMode() && this.platformService.isBrowser && couldNotRetrievePageUrl) {
			console.warn(
				`❌ Could not retrieve the customer support URL for this brand.\nMake sure there is a value associated with the "${CUSTOMER_SUPPORT_URL_KEY.desktop}" and "${CUSTOMER_SUPPORT_URL_KEY.mobile}" keys in CRS.`
			);
		}

		this._pageURL = pageURL;
	}

	private getPageURLTranslationKey(): string {
		const isDesktop = this.deviceConfigService.isDesktop();
		return isDesktop ? CUSTOMER_SUPPORT_URL_KEY.desktop : CUSTOMER_SUPPORT_URL_KEY.mobile;
	}
}
